/**
 * AI Dashboard Widget Component
 * ==============================
 * Real-time metrics and analytics for AI performance
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Box,
  Typography,
  Chip,
  Stack,
  LinearProgress,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  SmartToy,
  Person,
  TrendingUp,
  Warning,
  Schedule,
  Refresh,
  CheckCircle,
  Speed,
  Timer
} from '@mui/icons-material';
import api from '../../services/api';

interface AIMetrics {
  total_conversations: number;
  ai_conversations: number;
  human_conversations: number;
  escalated_conversations: number;
  average_confidence: number;
  average_response_time_ms: number;
  business_hours_active: boolean;
  business_hours_status?: string;
  pending_escalations: number;
  resolved_escalations: number;
  ai_success_rate: number;
}

export const AIDashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<AIMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  useEffect(() => {
    loadMetrics();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const response = await api.get('/analytics/ai-metrics');
      setMetrics(response.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to load AI metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!metrics && loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!metrics) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">Failed to load AI metrics</Typography>
        </CardContent>
      </Card>
    );
  }

  const aiPercentage = metrics.total_conversations > 0
    ? (metrics.ai_conversations / metrics.total_conversations) * 100
    : 0;

  const humanPercentage = metrics.total_conversations > 0
    ? (metrics.human_conversations / metrics.total_conversations) * 100
    : 0;

  return (
    <Card>
      <CardHeader
        avatar={<SmartToy color="primary" />}
        title="AI Performance Dashboard"
        subheader={`Last updated: ${lastUpdate.toLocaleTimeString()}`}
        action={
          <Tooltip title="Refresh">
            <IconButton onClick={loadMetrics} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        }
      />
      <CardContent>
        <Grid container spacing={3}>
          {/* Conversation Distribution */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom fontWeight={600}>
              Active Conversations
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" color="primary">
                    AI Handling
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {metrics.ai_conversations} ({aiPercentage.toFixed(0)}%)
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={aiPercentage}
                  color="primary"
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Human Handling
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {metrics.human_conversations} ({humanPercentage.toFixed(0)}%)
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={humanPercentage}
                  color="inherit"
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Metrics Grid */}
          <Grid item xs={6} md={3}>
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                textAlign: 'center'
              }}
            >
              <Speed color="primary" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h5" fontWeight={600}>
                {(metrics.average_confidence * 100).toFixed(0)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Avg Confidence
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={3}>
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                textAlign: 'center'
              }}
            >
              <Timer color="info" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h5" fontWeight={600}>
                {(metrics.average_response_time_ms / 1000).toFixed(1)}s
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Avg Response Time
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={3}>
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                textAlign: 'center'
              }}
            >
              <Warning color="warning" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h5" fontWeight={600}>
                {metrics.pending_escalations}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pending Escalations
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={3}>
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                textAlign: 'center'
              }}
            >
              <CheckCircle color="success" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h5" fontWeight={600}>
                {(metrics.ai_success_rate * 100).toFixed(0)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                AI Success Rate
              </Typography>
            </Box>
          </Grid>

          {/* Business Hours Status */}
          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                p: 2,
                backgroundColor: metrics.business_hours_active ? 'success.light' : 'warning.light',
                borderRadius: 2
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Schedule
                  color={metrics.business_hours_active ? 'success' : 'warning'}
                />
                <Typography variant="body1" fontWeight={600}>
                  Business Hours Status
                </Typography>
              </Stack>
              <Chip
                label={metrics.business_hours_active ? 'Active' : 'Outside Hours'}
                color={metrics.business_hours_active ? 'success' : 'warning'}
                size="small"
              />
            </Stack>
            {metrics.business_hours_status && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1 }}
              >
                {metrics.business_hours_status}
              </Typography>
            )}
          </Grid>

          {/* Escalation Summary */}
          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom fontWeight={600}>
              Escalation Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Stack alignItems="center" spacing={0.5}>
                  <Typography variant="h6" color="warning.main">
                    {metrics.escalated_conversations}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Escalated
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={4}>
                <Stack alignItems="center" spacing={0.5}>
                  <Typography variant="h6" color="error.main">
                    {metrics.pending_escalations}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Pending
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={4}>
                <Stack alignItems="center" spacing={0.5}>
                  <Typography variant="h6" color="success.main">
                    {metrics.resolved_escalations}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Resolved
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default AIDashboard;
